import { toTenantDate } from '@utils/timeZone';
import { useEffect } from 'react';
import { formatTime } from '@utils/i18n';
import { cn } from '@utils/cn';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { getMultiDayPosition } from '@contexts/scheduler/sidebar/helpers';
import { SchedulerGridScheduleMenu } from '../menu';
import { SchedulerGridScheduleElementProps } from './types';
import { SchedulerGridScheduleEdge } from '../edge';
import { useSchedulerContext } from '@contexts/scheduler';
import { useElementStyle } from '@hooks/scheduler/elementStyle';
import { SchedulerGridScheduleResize } from '../resize';
import { useDndMonitor, useDraggable } from '@dnd-kit/core';
import { useSchedulerDragAndDrop } from '@hooks/scheduler/dnd';
import { activeStyles, styles } from '@utils/schedule/constants';
import { getScheduleState } from '@utils/schedule/helpers';
import { parseJobTitle, parseProjectAddress, parseProjectName } from '@utils/textHelpers';
import { useAccess } from '@hooks/access';

export const SchedulerGridScheduleElement = ({
  data,
  reschedulable = true,
  tooltip,
  setIsTruncated,
}: SchedulerGridScheduleElementProps) => {
  const {
    active: { schedule, mode, newValues },
  } = useSchedulerContext();
  const access = useAccess();

  const { handleDragStart, handleDragMove, handleDragEnd, handlePointer } =
    useSchedulerDragAndDrop();

  const { id, task, warehouseTask, startsAt, endsAt, status, foreman } = data;
  const name = task?.name || warehouseTask?.name || '';
  const projectIdentifiers = task?.job?.project
    ? [parseProjectName(task.job.project), parseProjectAddress(task.job.project)]
    : [];
  const title = [...projectIdentifiers, parseJobTitle(task?.job, warehouseTask?.warehouse)]
    .filter(Boolean)
    .filter((el, index, arr) => arr.indexOf(el) === index)
    .join(' - ');

  const isActiveSchedule = !tooltip && schedule?.id === id;
  const variant = task?.appearance?.color || 'default';

  const { attributes, listeners, setNodeRef, transform, node } = useDraggable({
    id,
    attributes: { role: 'div' },
    disabled: !isActiveSchedule || mode !== 'move' || !reschedulable || access.readOnly,
  });

  useDndMonitor(
    isActiveSchedule && mode === 'move'
      ? {
          onDragStart: () => handleDragStart(data),
          onDragEnd: () => handleDragEnd(foreman?.id || ''),
          onDragMove: () => handleDragMove(),
        }
      : {},
  );

  const elementStyle = useElementStyle(data?.id || 'new', isActiveSchedule, false);
  const sidebar = useSchedulerSidebar();
  const position = isActiveSchedule
    ? getMultiDayPosition({
        relatedMultischedules: sidebar?.relatedMultischedules,
        currentSchedule: sidebar?.currentSchedule,
        plannedDays: sidebar?.plannedDays,
      })
    : null;
  const continuation = position && {
    before: position.day > 1,
    after: position.day < position.total,
  };
  const hasLeftEdge =
    !isActiveSchedule && !tooltip && data?.task?.multischedule && !data?.task?.multischeduleFirst;
  const hasRightEdge =
    !isActiveSchedule && !tooltip && data?.task?.multischedule && !data?.task?.multischeduleLast;

  useEffect(() => {
    const element = node?.current;
    if (!element || !setIsTruncated || isActiveSchedule) return;
    const texts = Array.from(element.getElementsByTagName('span'));
    setIsTruncated(texts.some((el) => el.offsetWidth < el.scrollWidth));
  }, [node, elementStyle, setIsTruncated, isActiveSchedule]);

  if (!tooltip && !elementStyle) return null;

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  const { regular, menu } = isActiveSchedule
    ? activeStyles
    : styles[variant][getScheduleState(startsAt, status)];

  const startHour =
    isActiveSchedule && mode !== 'move' && newValues?.startsAt
      ? formatTime(newValues.startsAt)
      : formatTime(toTenantDate(startsAt));
  const endHour =
    isActiveSchedule && mode !== 'move' && newValues?.endsAt
      ? formatTime(newValues?.endsAt)
      : formatTime(toTenantDate(endsAt));

  return (
    <div
      onPointerEnter={(e) => handlePointer(isActiveSchedule, 'enter', e.clientX, e.clientY)}
      onPointerLeave={() => handlePointer(isActiveSchedule, 'leave')}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn([
        'z-10 flex flex-col justify-between whitespace-nowrap rounded border p-1 leading-tight tracking-tight shadow-task',
        tooltip ? 'h-[43px] px-2' : 'absolute bottom-[3px] pl-2',
        isActiveSchedule &&
          'scheduler-active-schedule z-20 h-[calc(50%-2.5px)] justify-center px-2 py-0',
        regular,
      ])}
      style={!tooltip && elementStyle ? { ...elementStyle, ...style } : undefined}>
      {isActiveSchedule ? (
        <>
          <div className="relative z-10 mt-[1px] flex h-full items-center justify-between gap-1">
            <span className="overflow-hidden font-bold whitespace-nowrap">
              {continuation?.before && <span className="mr-1 font-normal">←</span>}
              {`${[title, name].filter(Boolean).join(' - ')}`}
            </span>
            <span className="overflow-hidden text-xs leading-none whitespace-nowrap">
              {startHour} - {endHour}
              {continuation?.after && <span className="ml-1">→</span>}
            </span>
          </div>
          <SchedulerGridScheduleResize direction="left" />
          <SchedulerGridScheduleResize direction="right" />
        </>
      ) : (
        <>
          <div className="relative flex justify-between gap-1 text-xs">
            <span className="overflow-hidden">{name || title}</span>
            <span className={cn(['overflow-hidden', !tooltip && 'mr-5'])}>
              {startHour} - {endHour}
            </span>
            {!tooltip && (
              <SchedulerGridScheduleMenu
                data={data}
                reschedulable={reschedulable}
                className={cn([
                  menu,
                  Number(elementStyle?.width || 0) < 36 && 'left-1/2 -translate-x-1/2',
                ])}
              />
            )}
          </div>
          <span className="relative z-10 overflow-hidden font-bold leading-none">
            {title || name}
          </span>
          {hasLeftEdge && <SchedulerGridScheduleEdge position="left" styles={regular} />}
          {hasRightEdge && <SchedulerGridScheduleEdge position="right" styles={regular} />}
        </>
      )}
    </div>
  );
};
